import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CompaniesService } from '@services/companies.service';
import { CompanyModel } from '@data-layer/models/companies';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormControl, Validators } from '@angular/forms';
import { enumBondRequestState } from '@data-layer/enums/common';
import { BillsRepository, BondRequestRepository } from '@data-layer/repositories';
import { catchError, filter, finalize, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { BillItemPendingModel, BillModel } from '@data-layer/models/payments';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { PaymentBreakdownHelper } from '@helpers/payment-breakdown.helper';
import { enumCompanyBusinessType, enumCompanyPaymentType } from '@data-layer/enums/companies';
import {
  enumDocumentRelatedEntityType,
  enumDocumentRootEntityType,
  enumDocumentState,
  enumDocumentType,
} from '@data-layer/enums/documents';
import { DocumentsService } from '@services/documents.service';
import { DocumentModel } from '@data-layer/models/documents';
import { of, ReplaySubject, throwError } from 'rxjs';
import { enumBondRequestResultError } from '@app/modules/company/enums';
import { TranslateService } from '@ngx-translate/core';
import { HttpErrorResponse } from '@angular/common/http';
import { IPaymentBreakdownItemGroup } from '@interfaces/payment-breakdown-group';
import { CardApplyCouponComponent } from '@shared/cards/components/card-apply-coupon/card-apply-coupon.component';
import { enumPaymentState } from '@data-layer/enums/payments';
import { PaymentState } from '@app/modules/payment/services';
import { Location } from '@angular/common';
import { ICustomError } from '@shared/result/interfaces';

@Component({
  selector: 'app-company-bond-request-view-page',
  templateUrl: './company-bond-request-view-page.component.html',
  styleUrls: ['./company-bond-request-view-page.component.scss'],
})
export class CompanyBondRequestViewPageComponent implements OnInit, OnDestroy {
  public company: CompanyModel;
  public checkedFormControl: UntypedFormControl = new UntypedFormControl(false, Validators.requiredTrue);
  public pendingBills: BillItemPendingModel[] = [];
  public paymentBreakdown: IPaymentBreakdownItemGroup[] = [];
  public poaDocument: DocumentModel;
  public isLoadingCompanyPoa: boolean = false;

  public isNotMatchedCompanyName: boolean = false;
  public isNotMatchedCompanyBusinessType: boolean = false;
  public isNotMatchedCompanyAddress: boolean = false;
  public isNotMatchedCompanyIdentifier: boolean = false;
  public companyAddressLine: string;
  public companyIdentifierValue: string;
  public couponName: string;

  @ViewChild(CardApplyCouponComponent) private cardApplyCouponComponentRef: CardApplyCouponComponent;

  private isListenAlready: boolean;
  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private companiesService: CompaniesService,
    private router: Router,
    private bondRequestRepository: BondRequestRepository,
    private ga: GoogleAnalyticsService,
    private billsRepository: BillsRepository,
    private documentsService: DocumentsService,
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private paymentState: PaymentState,
    private location: Location,
  ) {}

  public ngOnInit(): void {
    this.companiesService.companyChange$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.company = this.companiesService.getCompany();
      if (
        !this.company.bondRequest ||
        [enumBondRequestState.COMPLETED, enumBondRequestState.CUSTOM].includes(this.company.bondRequest.state)
      ) {
        this.router.navigate(
          ['/companies', 'bond-request', 'edit'],
          this.activatedRoute.snapshot.queryParams['saveParams'] ? { queryParamsHandling: 'merge' } : undefined,
        );
        return;
      }
      this.getPendingBills();
      this.getCompanyPoaDocument().pipe(takeUntil(this.destroy$)).subscribe();
      this.checkMatchedCompanyInfo();
    });
  }

  public ngOnDestroy(): void {
    this.paymentState.resetPaymentState();
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public get businessTypeLabel(): string {
    return enumCompanyBusinessType[this.company.bondRequest.continuousBondDetails.businessType];
  }

  public get companyBusinessTypeLabel(): string {
    return enumCompanyBusinessType[this.company.businessType];
  }

  public get isCanEdit(): boolean {
    return this.company.bondRequest.state === enumBondRequestState.CREATED;
  }

  public get isPaid(): boolean {
    return this.paymentBreakdown.length === 0;
  }

  public get isOnlyMonthlyPayments(): boolean {
    return this.pendingBills.every((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY);
  }

  public get isSent(): boolean {
    return [enumBondRequestState.SENT].includes(this.company.bondRequest.state);
  }

  public get isValid(): boolean {
    return this.checkedFormControl.valid && !!this.poaDocument;
  }

  public get isHasInstantPayment(): boolean {
    return (this.pendingBills || []).some((bill) => bill.paymentType === enumCompanyPaymentType.INSTANT);
  }

  public onSuccessPayment() {
    this.bondRequestRepository
      .sent(this.company.bondRequest.id)
      .pipe(
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        () => {
          this.openResultPage();
        },
        () => this.openResultPage(enumBondRequestResultError.SENDING),
      );
  }

  public onErrorPayment() {
    this.openResultPage(enumBondRequestResultError.PAYMENT);
  }

  public sentRequest() {
    this.createMonthlyBill()
      .pipe(
        switchMap(() => this.bondRequestRepository.sent(this.company.bondRequest.id)),
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        takeUntil(this.destroy$),
      )
      .subscribe(
        () => {
          this.openResultPage();
        },
        () => {
          this.openResultPage(enumBondRequestResultError.SENDING);
        },
      );
  }

  public updateDocumentState(state: enumDocumentState) {
    this.poaDocument.state = state;
  }

  public uploadPoa(file: File) {
    this.isLoadingCompanyPoa = true;
    this.documentsService
      .uploadDocument(
        {
          fileName: file.name,
          relatedEntityId: this.company.id,
          relatedEntityType: enumDocumentRelatedEntityType.COMPANY,
          rootEntityId: this.company.id,
          rootEntityType: enumDocumentRootEntityType.COMPANY,
          type: enumDocumentType.POWER_OF_ATTORNEY,
        },
        file,
      )
      .pipe(
        switchMap(() => this.getCompanyPoaDocument(false)),
        finalize(() => (this.isLoadingCompanyPoa = false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public setCompanyBusinessName() {
    this.bondRequestRepository
      .update(this.company.bondRequest.id, {
        ...this.company.bondRequest,
        continuousBondDetails: { ...this.company.bondRequest.continuousBondDetails, companyName: this.company.businessName },
      })
      .pipe(
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public setCompanyBusinessType() {
    this.bondRequestRepository
      .update(this.company.bondRequest.id, {
        ...this.company.bondRequest,
        continuousBondDetails: { ...this.company.bondRequest.continuousBondDetails, businessType: this.company.businessType },
      })
      .pipe(
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public setCompanyAddress() {
    this.bondRequestRepository
      .update(this.company.bondRequest.id, {
        ...this.company.bondRequest,
        continuousBondDetails: { ...this.company.bondRequest.continuousBondDetails, address: this.companyAddressLine },
      })
      .pipe(
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public setCompanyIdentifier() {
    this.bondRequestRepository
      .update(this.company.bondRequest.id, {
        ...this.company.bondRequest,
        continuousBondDetails: { ...this.company.bondRequest.continuousBondDetails, importerNumber: this.companyIdentifierValue },
      })
      .pipe(
        switchMap(() => this.companiesService.getCompanyAsync(true)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  public applyCoupon(couponName: string): void {
    this.couponName = couponName;
    this.getPendingBills();
  }

  private checkMatchedCompanyInfo() {
    this.isNotMatchedCompanyName = this.company.bondRequest.continuousBondDetails.companyName !== this.company.businessName;
    this.isNotMatchedCompanyBusinessType = this.company.bondRequest.continuousBondDetails.businessType !== this.company.businessType;

    this.companyAddressLine = [
      [this.company?.address?.line1, this.company?.address?.line2, this.company?.address?.line3].join(' '),
      this.company.address.city,
      [this.company.address.state.code, this.company.address.postalCode].join(' '),
      this.company.address.country.description,
    ]
      .filter((item: string) => !!item?.trim())
      .join(', ');
    this.isNotMatchedCompanyAddress = this.company.bondRequest.continuousBondDetails.address !== this.companyAddressLine;

    this.companyIdentifierValue = [
      this.translateService.instant('IDENTIFIER_QUALIFIER.' + this.company.identifierQualifier),
      this.company.identifier,
    ].join(' ');
    this.isNotMatchedCompanyIdentifier = this.companyIdentifierValue !== this.company.bondRequest.continuousBondDetails.importerNumber;
  }

  private openResultPage(error?: enumBondRequestResultError) {
    const returnUrl: string = this.activatedRoute.snapshot.queryParams['returnUrl'];
    if ([undefined, null].includes(error) && returnUrl) {
      this.router.navigateByUrl(returnUrl);
      return;
    }
    this.router.navigate(
      ['/companies', 'bond-request', 'result'],
      ![undefined, null].includes(error) ? { queryParams: { error: JSON.stringify(error) } } : undefined,
    );
  }

  private getPendingBills() {
    this.billsRepository
      .pendingBills(this.company.id, {
        referenceNumbers: [this.company.bondRequest.referenceNumber],
        coupon: this.couponName,
        paymentProvider: null,
        paymentCodeId: null,
      })
      .pipe(
        catchError((error) => {
          this.couponName = null;
          return this.handleBillError(error);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((pendingBills) => {
        this.pendingBills = pendingBills;
        this.paymentBreakdown = PaymentBreakdownHelper.buildPaymentBreakdownFromPendingBills(this.company.bondRequest, pendingBills, []);

        if (!this.isListenAlready) {
          this.listenPaymentActions();
        }
      });
  }

  private getCompanyPoaDocument(isShowLoader: boolean = true) {
    return this.documentsService.getCompanyDocuments(this.company.id, isShowLoader).pipe(
      tap((documents) => {
        this.poaDocument = documents.find((document) => document.type === enumDocumentType.POWER_OF_ATTORNEY);
      }),
    );
  }

  private createMonthlyBill() {
    if (this.pendingBills.some((bill) => bill.paymentType === enumCompanyPaymentType.MONTHLY)) {
      return this.billsRepository
        .create(this.company.id, {
          referenceNumbers: [this.company.bondRequest.referenceNumber],
          coupon: this.couponName,
          paymentProvider: null,
          paymentCodeId: null,
        })
        .pipe(
          catchError((error) => {
            return this.handleBillError(error);
          }),
        );
    }
    return of(null);
  }

  private handleBillError(error: HttpErrorResponse) {
    const errorMessage = this.billsRepository.getErrorMessage(error);
    this.cardApplyCouponComponentRef.setCouponError(errorMessage);
    return throwError(error);
  }

  private listenPaymentActions(): void {
    this.paymentState.successPayment$
      .pipe(
        filter((data: { state: enumPaymentState; bill: BillModel }) => !!data),
        take(1),
      )
      .subscribe(() => {
        this.onSuccessPayment();
      });

    this.paymentState.errorCustomPayment$
      .pipe(
        filter((error: ICustomError) => !!error),
        take(1),
      )
      .subscribe(() => {
        this.onErrorPayment();
      });

    this.paymentState.errorCallback$
      .pipe(
        filter((error: ICustomError) => !!error),
        take(1),
      )
      .subscribe(() => {
        this.onErrorPayment();
      });
  }
}
